import { FC } from 'react'
import { flushSync } from 'react-dom'
import type { LeadProfileLead } from '../../LeadProfileModal.types'
import {
  InfoCircleOutlined,
  UserAddOutlined,
  UserDeleteOutlined,
} from '@ant-design/icons'
import classes from './index.module.css'
import { handlerDeleteStudAccount } from './deleteStudAccountModalFn'
import { useAddAlert } from '@/hooks/useAddAlert'
import { useFullLeadModalAccess } from '../../hooks/useFullLeadModalAccess'
import { useLeadRefetch } from '../../LeadRefetchContext'
import Button from '@UI/New/Button'
import TooltipWrapper from '@UI/New/TooltipWrapper/TooltipWrapper'

const PENDING_PAYMENT_BIND_HINT =
  'Привязка другого ЛК недоступна: лид зарезервирован под оплату покупки из каталога. Станет доступна, когда оплата завершится.'

interface TabBindingStudAccountProps {
  lead: LeadProfileLead
  onOpenStudentAccount: () => void
  onOpenAddStudAccount: () => void
  onRefetchLead: () => void
}

const isPendingPaymentAccount = (lead: LeadProfileLead): boolean =>
  lead.account_state === 'pending_payment'

/** Открывать ЛК только по флагу бэка; без поля — legacy по user_id. */
const canOpenStudentCard = (lead: LeadProfileLead): boolean => {
  if (lead.can_open_student_card === true) return true
  if (lead.can_open_student_card === false) return false
  return Boolean(lead.user_id)
}

const TabBindingStudAccount: FC<TabBindingStudAccountProps> = ({
  lead,
  onOpenStudentAccount,
  onOpenAddStudAccount,
  onRefetchLead,
}) => {
  const addAlert = useAddAlert()
  const access = useFullLeadModalAccess()
  const { patchLead } = useLeadRefetch()

  if (isPendingPaymentAccount(lead)) {
    return (
      <div className={classes.studAccountTabWrapper}>
        <div
          className={classes.pendingPaymentChip}
          role="status"
          aria-label="ЛК будет создан после оплаты"
        >
          <span className={classes.pendingPaymentLabel}>
            ЛК будет создан после оплаты
          </span>
          <TooltipWrapper title={PENDING_PAYMENT_BIND_HINT} placement="bottom">
            <button
              type="button"
              className={classes.pendingPaymentInfoButton}
              aria-label="Почему нельзя привязать другой ЛК"
            >
              <InfoCircleOutlined
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              />
            </button>
          </TooltipWrapper>
        </div>
        {access.canBindUnbindStudent ? (
          <TooltipWrapper title={PENDING_PAYMENT_BIND_HINT} placement="bottom">
            <span className={classes.pendingBindWrap}>
              <Button
                type="button"
                color="primary"
                className={classes.bindLkButton}
                disabled
                leftContent={
                  <UserAddOutlined
                    onPointerEnterCapture={undefined}
                    onPointerLeaveCapture={undefined}
                  />
                }
              >
                Привязать ЛК студента
              </Button>
            </span>
          </TooltipWrapper>
        ) : null}
      </div>
    )
  }

  if (canOpenStudentCard(lead)) {
    return (
      <div className={classes.studAccountTabWrapper}>
        <div
          className={classes.boundActions}
          role="group"
          aria-label="Личный кабинет ученика"
        >
          <Button
            type="button"
            color="primary"
            className={classes.goToLkButton}
            onClick={() => onOpenStudentAccount()}
          >
            Перейти в ЛК ученика
          </Button>
          {access.canBindUnbindStudent && (
            <TooltipWrapper title="Отвязать ЛК ученика" placement="top">
              <Button
                type="button"
                color="danger"
                className={classes.unbindIconButton}
                classNames={{ button: classes.unbindIconButtonInner }}
                aria-label="Отвязать личный кабинет ученика"
                leftContent={
                  <UserDeleteOutlined
                    onPointerEnterCapture={undefined}
                    onPointerLeaveCapture={undefined}
                  />
                }
                onClick={() =>
                  handlerDeleteStudAccount({
                    lead,
                    addAlert,
                    onSuccess: ({ detachParents }) => {
                      flushSync(() => {
                        patchLead({
                          user_id: null,
                          user: null,
                          account_state: 'unlinked',
                          can_open_student_card: false,
                          ...(detachParents ? { parents: [] } : {}),
                        })
                      })
                      onRefetchLead()
                    },
                  })
                }
              />
            </TooltipWrapper>
          )}
        </div>
      </div>
    )
  }

  if (!access.canBindUnbindStudent) {
    return null
  }

  return (
    <div className={classes.studAccountTabWrapper}>
      <Button
        type="button"
        color="primary"
        leftContent={
          <UserAddOutlined
            onPointerEnterCapture={undefined}
            onPointerLeaveCapture={undefined}
          />
        }
        className={classes.bindLkButton}
        onClick={() => onOpenAddStudAccount()}
      >
        Привязать ЛК студента
      </Button>
    </div>
  )
}

export default TabBindingStudAccount
